import React from "react";
import { ArrowLeft } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { ProgressBar } from "@/components/revanche/ProgressBar";
import { PrimaryCTA } from "@/components/revanche/PrimaryCTA";
import { EASE, STAGGER } from "@/lib/design";

// Gabarit unique d'écran de questionnaire.
// Haut : flèche retour + barre de progression 3px. Corps : titre, sous-titre, options.
// Bas : un seul bouton (optionnel), 24px au-dessus de la zone sécurisée.
export const StepShell = ({
    questionNumber,
    totalQuestions,
    onBack,
    title,
    explanation,
    children,
    showCta = false,
    onContinue,
    continueDisabled,
    ctaLabel = "Continuer",
    scrollable = false,
}) => {
    const reduce = useReducedMotion();
    const container = {
        animate: { transition: { staggerChildren: reduce ? 0 : STAGGER } },
    };
    const item = {
        initial: { opacity: 0, y: reduce ? 0 : 8 },
        animate: { opacity: 1, y: 0, transition: { duration: reduce ? 0.12 : 0.18, ease: EASE } },
    };

    return (
        <div className="absolute inset-0 flex flex-col">
            {/* Haut : retour + progression */}
            <div className="shrink-0 safe-top" style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 12 }}>
                <div className="flex items-center mb-3">
                    <button
                        type="button"
                        onClick={onBack}
                        data-testid="btn-back"
                        aria-label="Retour"
                        className="tap-feedback w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full active:scale-[0.98]"
                    >
                        <ArrowLeft size={22} strokeWidth={1.5} color="var(--text)" />
                    </button>
                </div>
                <ProgressBar value={questionNumber} max={totalQuestions} />
            </div>

            {/* Corps */}
            <motion.div
                variants={container}
                initial="initial"
                animate="animate"
                className={`flex-1 min-h-0 flex flex-col ${scrollable ? "overflow-y-auto scrollbar-none" : "overflow-hidden"}`}
                style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 24 }}
            >
                <motion.h1
                    variants={item}
                    data-testid="question-title"
                    className="font-body"
                    style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.15, color: "var(--text)" }}
                >
                    {title}
                </motion.h1>
                {explanation ? (
                    <motion.p
                        variants={item}
                        className="font-body"
                        style={{ marginTop: 8, fontSize: "16px", fontWeight: 400, color: "var(--text-secondary)" }}
                    >
                        {explanation}
                    </motion.p>
                ) : null}
                <motion.div variants={item} className="flex-1 min-h-0 flex flex-col" style={{ marginTop: 32 }}>
                    {children}
                </motion.div>
            </motion.div>

            {/* Bas : bouton unique optionnel */}
            {showCta ? (
                <div className="shrink-0 safe-bottom" style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24, paddingTop: 8 }}>
                    <PrimaryCTA dataTestId="btn-continue" onClick={onContinue} disabled={continueDisabled}>
                        {ctaLabel}
                    </PrimaryCTA>
                </div>
            ) : null}
        </div>
    );
};
